
import { useState } from "react";
import { EmailRecord } from "@/types/email";
import { Investor, Contact } from "@/types/investor";
import { 
  Card,
  CardHeader,
  CardTitle,
  CardDescription,
  CardContent,
  CardFooter
} from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { ChevronLeft, Save, Send, Trash2 } from "lucide-react";

interface DraftDetailsEditorProps {
  draft: EmailRecord;
  investor?: Investor;
  contact?: Contact;
  onClose: () => void;
  onUpdate: (updatedDraft: EmailRecord) => Promise<EmailRecord[]>;
  onDelete: (draft: EmailRecord) => void;
  onMarkAsSent: (draft: EmailRecord) => void;
}

const DraftDetailsEditor = ({ 
  draft, 
  investor, 
  contact, 
  onClose, 
  onUpdate,
  onDelete,
  onMarkAsSent
}: DraftDetailsEditorProps) => {
  const [editedDraft, setEditedDraft] = useState<EmailRecord>({...draft});
  const [isSaving, setIsSaving] = useState(false);
  
  const handleSave = async () => {
    setIsSaving(true);
    try {
      await onUpdate(editedDraft);
    } finally {
      setIsSaving(false);
    }
  };
  
  return (
    <div className="space-y-4">
      <Button 
        variant="ghost" 
        size="sm" 
        onClick={onClose}
        className="flex items-center gap-1"
      >
        <ChevronLeft className="h-4 w-4" />
        Back to drafts
      </Button>
      
      <Card>
        <CardHeader>
          <CardTitle className="flex items-start justify-between">
            <span>Edit Draft</span>
            <div className="text-base font-normal">
              {investor?.firm && (
                <span className="bg-blue-50 text-blue-700 px-2 py-1 rounded-md">
                  {investor.firm}
                </span>
              )}
            </div>
          </CardTitle>
          <CardDescription>
            Draft for {contact?.name || "Unknown Contact"}
          </CardDescription>
        </CardHeader>
        
        <CardContent className="space-y-4">
          <div className="space-y-2">
            <label className="text-sm font-medium">Draft Label</label>
            <Input 
              value={editedDraft.label || ""} 
              onChange={(e) => setEditedDraft({...editedDraft, label: e.target.value})} 
              placeholder="Enter a label for this draft"
            />
          </div>
          
          <div className="space-y-2">
            <label className="text-sm font-medium">Subject</label>
            <Input 
              value={editedDraft.subject} 
              onChange={(e) => setEditedDraft({...editedDraft, subject: e.target.value})} 
              placeholder="Email subject"
            />
          </div>
          
          <div className="space-y-2">
            <label className="text-sm font-medium">Content</label>
            <Textarea 
              value={editedDraft.body} 
              onChange={(e) => setEditedDraft({...editedDraft, body: e.target.value})} 
              rows={15} 
              placeholder="Email content"
              className="font-mono text-sm"
            />
          </div>
        </CardContent>
        
        <CardFooter className="flex justify-between">
          <Button 
            variant="outline" 
            onClick={() => onDelete(draft)}
            className="text-red-500"
          >
            <Trash2 className="h-4 w-4 mr-2" />
            Delete Draft
          </Button>
          
          <div className="space-x-2">
            <Button 
              variant="outline" 
              onClick={handleSave}
              disabled={isSaving}
            >
              <Save className="h-4 w-4 mr-2" />
              Save Changes
            </Button>
            
            <Button 
              variant="default"
              onClick={() => onMarkAsSent(editedDraft)}
              className="bg-green-600 hover:bg-green-700"
            >
              <Send className="h-4 w-4 mr-2" />
              Mark as Sent
            </Button>
          </div>
        </CardFooter>
      </Card>
    </div>
  );
};

export default DraftDetailsEditor;
